import { FC, useMemo } from 'react';
import { useAtom } from 'jotai';
import { InlineNotification, Tag } from '@carbon/react';
import searchAtomPair, { type SearchParams } from 'atoms/search-atom';
import { ConfidenceBreakdown } from 'components/confidence-breakdown';
import { formatConfidence } from 'utils/number';
import {
  AuditLogTable,
  AgentRunDetail,
  EventExpanded,
} from 'pages/documents/structuring/audit-log';
import style from 'pages/documents/structuring/audit-log/audit-log.module.scss';
import type {
  RcActivityFeedAgentRunRow,
  RcActivityFeedItem,
  RcActivityFeedRequest,
  RcActivityFeedResponse,
  RcActivityFeedSection,
  RcActivityFeedSuggestion,
} from './types-activity-feed';

// RC structuring activity log. The RC activity feed
// (POST /queries/royalty_check_structuring_activity_feeds) serializes the same
// agent_run + event envelope as the AFE / DO / Entity-Match feeds, so this
// component reuses the shared audit-log renderers
// (pages/documents/structuring/audit-log) and owns only its own data fetch via
// `searchAtomPair`. Mirror of DO's agent-audit-log, with the DO section vocab
// (overview/interest/well) swapped for the RC vocab (overview/line_item/summary/
// well) and the scope keys swapped for royalty_check_target_id /
// royalty_line_item_id.
//
// Well-section agent runs persist suggestion rows (confidence_factors +
// review_reason), rendered via the same SuggestionsList / ConfidenceBreakdown
// convention the AFE/DO well logs use. The other sections carry no suggestions
// and render the empty state.

type FeedRequestParams = SearchParams & RcActivityFeedRequest;

// Well-match suggestion row — mirror of the AFE/DO well log's row (same Well
// suggestion shape) reusing the shared, exported ConfidenceBreakdown. Pure (no
// atoms / no data coupling); lives here per the established per-page convention.
const WellMatchSuggestionRow: FC<{ suggestion: RcActivityFeedSuggestion }> = ({
  suggestion: s,
}) => (
  <div className={style.suggestionItem}>
    <div className={style.suggestionHeader}>
      <span className={style.suggestionName}>
        {s.suggestedWellName || `Well #${s.suggestedWellId ?? '—'}`}
      </span>
      <div className={style.suggestionMeta}>
        <span className={style.suggestionScore}>
          {formatConfidence(s.confidenceScore)}
        </span>
        <Tag type="gray" size="sm">
          {s.status}
        </Tag>
      </div>
    </div>
    {s.reviewReason && (
      <p className={style.suggestionReason}>Review reason: {s.reviewReason}</p>
    )}
    {s.confidenceFactors && s.confidenceFactors.length > 0 && (
      <ConfidenceBreakdown factors={s.confidenceFactors} />
    )}
  </div>
);

const SuggestionsList: FC<{ rows: RcActivityFeedSuggestion[] }> = ({
  rows,
}) => {
  if (rows.length === 0)
    return (
      <p className={style.muted}>No suggestions were saved for this run.</p>
    );
  return (
    <div className={style.suggestionsList}>
      {rows.map((s) => (
        <WellMatchSuggestionRow key={s.id} suggestion={s} />
      ))}
    </div>
  );
};

interface Props {
  documentId: number | string;
  /**
   * RC structuring section to scope the feed to. Omit it for the consolidated
   * document-level log (the backend treats a blank section as "all sections").
   * The well drill-in passes 'well' to scope to a single target.
   */
  section?: RcActivityFeedSection;
  /**
   * Narrow the feed to one line item (its SF Royalty Line Item Id — a STRING).
   * Omitted shows the whole document's feed.
   */
  royaltyLineItemId?: string;
  /**
   * Narrow the feed to one RC target (its RoyaltyCheckTarget id). The Well Match
   * Review drill-in passes this to scope the per-well events + Well Matching
   * Agent runs to a single target. Omitted for the document-level log.
   */
  royaltyCheckTargetId?: number | string;
}

const renderAgentRun = (row: RcActivityFeedAgentRunRow) => {
  const d = row.data;
  return (
    <AgentRunDetail
      meta={d}
      runId={row.id}
      suggestionsLabel="Suggestions Persisted"
      suggestionsCount={d.suggestions?.length ?? 0}
      suggestions={<SuggestionsList rows={d.suggestions ?? []} />}
    />
  );
};

const renderExpanded = (row: RcActivityFeedItem) => {
  if (row.activityType === 'agent_run') return renderAgentRun(row);
  return (
    <EventExpanded
      user={row.data.user}
      action={row.data.action}
      description={row.data.description}
    />
  );
};

export const AgentAuditLog: FC<Props> = ({
  documentId,
  section,
  royaltyLineItemId,
  royaltyCheckTargetId,
}) => {
  const { searchAtom } = useMemo(
    () =>
      searchAtomPair<RcActivityFeedItem, FeedRequestParams>(
        { href: '/queries/royalty_check_structuring_activity_feeds' },
        {
          query: {
            document_id: documentId,
            // Omit `section` entirely when unset so the BE merges every section's
            // events + the agent runs into one feed (the document-level log).
            ...(section ? { section } : {}),
            // Scope to one line / target when given (omit the key otherwise so
            // the BE's `.present?` narrowing check sees nothing).
            ...(royaltyLineItemId !== null && royaltyLineItemId !== undefined
              ? { royalty_line_item_id: royaltyLineItemId }
              : {}),
            ...(royaltyCheckTargetId !== null &&
            royaltyCheckTargetId !== undefined
              ? { royalty_check_target_id: royaltyCheckTargetId }
              : {}),
            page: 1,
            page_size: 50,
            sort_direction: 'desc',
          },
        }
      ),
    [documentId, section, royaltyLineItemId, royaltyCheckTargetId]
  );

  const [searchState] = useAtom(searchAtom);

  const activities =
    (searchState.data as unknown as RcActivityFeedResponse | undefined)
      ?.results ?? [];

  if (searchState.error && activities.length === 0) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Unable to load activity"
        subtitle={searchState.error}
        style={{ maxWidth: '100%' }}
      />
    );
  }

  if (searchState.loading && activities.length === 0) {
    return <p className={style.empty}>Loading activity…</p>;
  }

  return (
    <AuditLogTable
      rows={activities}
      renderExpanded={renderExpanded}
      emptyMessage="No activity yet."
    />
  );
};
